import {
  Box,
  DrawerCloseButton,
  DrawerContent,
  Flex,
  Icon,
  IconButton,
  InputGroup,
  InputLeftAddon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import debounce from 'lodash/debounce';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CellValue } from 'react-table';

import { IconPen, IconTrashbin } from 'assets/icons';
import { ControllableDrawer } from 'components/Drawer';
import { ConfirmationModal } from 'components/Modal';
import ReactSelect from 'components/ReactSelect';
import SearchField from 'components/SearchField';
import Table, { IndeterminateCheckbox } from 'components/Table';
import HolidayForm from 'containers/CompanySettings/TimeOffSetup/RightDrawer/HolidayForm';
import { initialHolidayData } from 'containers/CompanySettings/TimeOffSetup/RightDrawer/HolidayForm/schema';
import { statusOptions } from 'global/options';
import { useBulkDisableHolidays } from 'hooks/timeOff/useBulkDisableHolidays';
import { useBulkEnableHolidays } from 'hooks/timeOff/useBulkEnableHolidays';
import { IDeleteHolidays, useDeleteHolidays } from 'hooks/timeOff/useDeleteHolidays';
import { IHolidaysSearchQuery, useFetchPaginatedHolidays } from 'hooks/timeOff/useFetchPaginatedHolidays';
import { formatYearMonthDay } from 'utils/format';

import TableFooter from './TableFooter';

const initialHolidaysQuery: IHolidaysSearchQuery = {
  page: 1,
  page_size: 25,
  search: '',
  active: '1',
};

const Holidays: React.FC<unknown> = () => {
  const { t } = useTranslation(['timeOff', 'common']);

  const [dialogIds, setDialogIds] = useState<IDeleteHolidays>({ ids: [] });
  const [searchQuery, setSearchQuery] = useState<IHolidaysSearchQuery>(initialHolidaysQuery);
  const [formData, setFormData] = useState<typeof initialHolidayData>(initialHolidayData);

  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();
  const { isOpen: isEnableModalOpen, onOpen: onEnableModalOpen, onClose: onEnableModalClose } = useDisclosure();
  const { isOpen: isDisableModalOpen, onOpen: onDisableModalOpen, onClose: onDisableModalClose } = useDisclosure();
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure();

  const { status: holidaysStatus, data: holidaysData, refetch } = useFetchPaginatedHolidays(searchQuery);
  const { status: deleteStatus, mutateAsync: mutateDeleteAsync } = useDeleteHolidays();
  const { status: enableHolidaysStatus, mutateAsync: mutateAsyncEnableHolidays } = useBulkEnableHolidays();
  const { status: disableHolidaysStatus, mutateAsync: mutateAsyncDisableHolidays } = useBulkDisableHolidays();

  const handleEditIconClick = (holidayData: any) => {
    const formData = {
      id: holidayData?.id,
      uuid: holidayData?.uuid,
      name: holidayData?.name,
      active: holidayData?.active,
      start_date: holidayData?.start_date ? new Date(holidayData.start_date) : null,
      end_date: holidayData?.end_date ? new Date(holidayData.end_date) : null,
      users: holidayData?.users,
      repeat_yearly: holidayData?.repeat_yearly || false,
      on_calendar: holidayData?.on_calendar || false,
      group_ids: [],
    };
    setFormData(formData);
    onDrawerOpen();
  };

  const columns = useMemo(
    () => [
      {
        id: 'selection',
        width: 30,
        Header: ({ getToggleAllRowsSelectedProps }: any) => {
          return <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} colorScheme="secondary" />;
        },
        Cell: ({ row }: any) => {
          return <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} colorScheme="secondary" />;
        },
      },
      {
        Header: 'Holiday',
        accessor: 'name',
        Cell: (data: CellValue) => {
          return (
            <div>
              <Text fontWeight="semibold" color="primary.500" textTransform="capitalize">
                {data.row.original?.name}
              </Text>
            </div>
          );
        },
      },
      {
        Header: t('common:status'),
        accessor: 'active',
        width: 100,
        Cell: (data: CellValue) => {
          return data.value ? (
            <Text color="success.500" fontWeight="semibold">
              {t('common:active')}
            </Text>
          ) : (
            <Text color="secondary.700" fontWeight="semibold">
              {t('common:inactive')}
            </Text>
          );
        },
      },
      {
        Header: 'Start Date',
        accessor: 'start_date',
        Cell: (data: CellValue) => <Text>{data.value ? formatYearMonthDay(data.value) : '- '}</Text>,
      },
      {
        Header: 'End Date',
        accessor: 'end_date',
        Cell: (data: CellValue) => <Text>{data.value ? formatYearMonthDay(data.value) : '- '}</Text>,
      },
      {
        Header: 'Applicable For',
        accessor: 'users',
        width: 200,
        Cell: (data: CellValue) => {
          const users = data.value;
          return (
            <Menu isLazy={true} lazyBehavior="unmount">
              <MenuButton textAlign="left">
                <Tag fontWeight="semibold" color="primary.500">
                  {Array.isArray(users) && users.length
                    ? users.length === 1
                      ? `${data.value[0]?.firstName} ${data.value[0]?.lastName}`
                      : `${data.value[0]?.firstName} ${data.value[0]?.lastName} and ${data.value.length - 1} others`
                    : 'All'}
                </Tag>
              </MenuButton>
              {users.length > 0 ? (
                <MenuList>
                  {users.map((item: any, index: number) => (
                    <MenuItem color="primary.500" fontWeight="600" key={index}>
                      {item?.firstName} {item?.lastName}
                    </MenuItem>
                  ))}
                </MenuList>
              ) : null}
            </Menu>
          );
        },
      },

      {
        Header: '',
        accessor: 'action',
        Cell: (data: CellValue) => {
          return (
            <div>
              <Tooltip label="Update Holiday" hasArrow bg="black">
                <IconButton
                  aria-label="Edit"
                  bg="rgba(38, 103, 201, 0.1)"
                  icon={<Icon as={IconPen} />}
                  size="sm"
                  mr="3"
                  _hover={{ bg: 'rgba(38, 103, 201, 0.2)' }}
                  onClick={() => handleEditIconClick(data.row.original)}
                />
              </Tooltip>
              <Tooltip label="Delete Holiday" hasArrow bg="black">
                <IconButton
                  aria-label="Delete"
                  bg="rgba(255, 83, 83, 0.1)"
                  icon={<Icon as={IconTrashbin} />}
                  size="sm"
                  _hover={{ bg: 'rgba(255, 83, 83, 0.2)' }}
                  onClick={() => handleSingleDelete(data.row.original.id)}
                />
              </Tooltip>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleDrawerClose = () => {
    setFormData(initialHolidayData);
    onDrawerClose();
  };

  // delete holiday handler
  const handleSingleDelete = (id: number) => {
    setDialogIds({ ids: [id] });
    onDeleteModalOpen();
  };

  const handleBulkDelete = (selectRows: any) => {
    const ids = selectRows.map((row: any) => row.original.id);
    setDialogIds({ ids: ids });
    onDeleteModalOpen();
  };

  const deleteHoliday = () => {
    mutateDeleteAsync(dialogIds).then(() => {
      onDeleteModalClose();
      setDialogIds({ ids: [] });
    });
  };

  //enable holidays handler

  const handleBulkEnable = (selectRows: any) => {
    const ids = selectRows.filter((row: any) => !row.original.active).map((row: any) => row.original.id);
    setDialogIds({ ids: ids });
    onEnableModalOpen();
  };

  const enableHolidays = () => {
    mutateAsyncEnableHolidays(dialogIds).then(() => {
      refetch();
      onEnableModalClose();
      setDialogIds({ ids: [] });
    });
  };

  //disable holidays handler
  const handleBulkDisable = (selectRows: any) => {
    const ids = selectRows.filter((row: any) => row.original.active).map((row: any) => row.original.id);
    setDialogIds({ ids: ids });
    onDisableModalOpen();
  };

  const disableHolidays = () => {
    mutateAsyncDisableHolidays(dialogIds).then(() => {
      refetch();
      onDisableModalClose();
      setDialogIds({ ids: [] });
    });
  };

  const handleCategoryStatusChange = (selectedOption: ILabelValueOptions) => {
    setSearchQuery({ ...searchQuery, page: 1, active: selectedOption?.value === 'active' ? '1' : '0' });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchInputChange = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value.toLowerCase().trim();
      setSearchQuery({ ...searchQuery, page: 1, search: searchValue });
    }, 400),
    [searchQuery]
  );

  const handlePageSizeChange = (pageSize: number) => {
    setSearchQuery(queries => ({ ...queries, page_size: pageSize, page: 1 }));
  };

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updatedSearchQuery = (previousSearchQuery: IHolidaysSearchQuery) => ({
      ...previousSearchQuery,
      page: selectedPageIndex + 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  return (
    <>
      <ControllableDrawer onClose={handleDrawerClose} isOpen={isDrawerOpen}>
        <DrawerContent>
          <DrawerCloseButton />
          <HolidayForm onClose={handleDrawerClose} formData={formData} />
        </DrawerContent>
      </ControllableDrawer>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={onDeleteModalClose}
        title="Are you sure you want to delete the holiday(s)?"
        subTitle="Once deleted, you will not be able to retrieve the holiday(s). (THIS CANNOT BE REVERSED)."
        confirmButtonLabel={t('common:delete')}
        confirmButtonProps={{
          bg: 'rgba(255, 83, 83, 0.1)',
          color: 'secondary.500',
          _hover: {
            bg: 'rgba(255, 83, 83, 0.2)',
          },
          isLoading: deleteStatus === 'loading',
          onClick: deleteHoliday,
        }}
      />

      <ConfirmationModal
        isOpen={isEnableModalOpen}
        onClose={onEnableModalClose}
        title={`${t('timeOff:enableHolidayModalTitle')}`}
        subTitle={t('timeOff:enableHolidayModalSubtitle')}
        confirmButtonLabel={t('common:activate')}
        modalProps={{ size: '3xl' }}
        confirmButtonProps={{
          bg: 'success.200',
          color: 'success.500',
          _hover: {
            bg: 'success.600',
            color: 'white',
          },
          isLoading: enableHolidaysStatus === 'loading',
          onClick: enableHolidays,
        }}
      />

      <ConfirmationModal
        isOpen={isDisableModalOpen}
        onClose={onDisableModalClose}
        title={`${t('timeOff:disableHolidayModalTitle')}`}
        subTitle={t('timeOff:disableHolidayModalSubtitle')}
        confirmButtonLabel={t('common:deactivate')}
        modalProps={{ size: '3xl' }}
        confirmButtonProps={{
          bg: 'rgba(255, 83, 83, 0.1)',
          color: 'secondary.500',
          _hover: {
            bg: 'rgba(255, 83, 83, 0.2)',
          },
          isLoading: disableHolidaysStatus === 'loading',
          onClick: disableHolidays,
        }}
      />

      <Flex
        flexDir={{ base: 'column', sm: 'row' }}
        alignItems="center"
        mb="5"
        gridGap="2"
        justifyContent="space-between"
      >
        <InputGroup maxWidth={{ base: '100%', sm: '180px' }} height="fit-content">
          <InputLeftAddon children="Status" px="2" />
          <ReactSelect
            options={statusOptions}
            size="md"
            fontSize="sm"
            isSearchable
            defaultValue={{ label: 'Active', value: 'active' }}
            placeholder="Select Status"
            onChange={handleCategoryStatusChange}
          />
        </InputGroup>
        <Box ml="auto" maxWidth={{ md: '350px' }} width="100%">
          <SearchField
            inputProps={{
              placeholder: 'Search Holiday',
              onChange: handleSearchInputChange,
            }}
          />
        </Box>
      </Flex>
      <Table
        columns={columns}
        data={holidaysData?.payload || []}
        paginationData={holidaysData?.meta_data?.pagination}
        isLoading={holidaysStatus === 'loading'}
        enableRowSelect
        onPageChange={handlePageChange}
        handlePageSizeChange={handlePageSizeChange}
        paginationStyle="rowsPerPage"
        emptyTableStateProps={{
          searchText: searchQuery.search,
        }}
        renderTableFooter={(selectedFlatRows: any, toggleAllRowSelected) => {
          return (
            <TableFooter
              selectedFlatRows={selectedFlatRows}
              toggleAllRowSelected={toggleAllRowSelected}
              handleBulkDelete={handleBulkDelete}
              handleBulkEnable={handleBulkEnable}
              handleBulkDisable={handleBulkDisable}
            />
          );
        }}
      />
    </>
  );
};
export default Holidays;
